<template>
  <el-card
    shadow="never"
    class="monitoring-block monitoring-statcard-block"
    :class="urgencyClass"
  >
    <div
      slot="header"
      class="monitoring-block__header"
    >
      <span class="title">{{ $t(definition.titleKey) }}</span>
      <MonitoringBlockHint v-if="!isStatic" />
    </div>

    <div class="monitoring-statcards">
      <div
        v-for="card in definition.cards"
        :key="card.key"
        class="monitoring-statcard"
        :class="
          card.severity
            ? `statcard-severity-${card.severity}`
            : `statcard-${card.key}`
        "
      >
        <i
          :class="card.icon"
          class="monitoring-statcard__icon"
        />
        <span class="monitoring-statcard__label">{{
          card.label || $t(card.labelKey)
        }}</span>
        <MonitoringSkeleton
          v-if="loading"
          class="monitoring-count-skeleton"
          variant="h1"
        />
        <span
          v-else-if="isStatic"
          class="monitoring-count monitoring-count--large monitoring-count--static"
        >
          {{ value(card) }}
        </span>
        <button
          v-else
          type="button"
          class="monitoring-count monitoring-count--large"
          :class="{
            'monitoring-count--zero': !alwaysClickable && value(card) === 0,
          }"
          :disabled="!alwaysClickable && value(card) === 0"
          @click="debouncedClick(card)"
        >
          {{ value(card) }}
        </button>
      </div>
    </div>
  </el-card>
</template>

<script>
  import debounce from 'lodash.debounce';
  import MonitoringBlockHint from './MonitoringBlockHint.vue';
  import MonitoringSkeleton from './MonitoringSkeleton.vue';

  export default {
    name: 'MonitoringStatCardBlock',
    components: { MonitoringBlockHint, MonitoringSkeleton },
    props: {
      definition: {
        type: Object,
        required: true,
      },
      data: {
        type: Object,
        default: () => ({}),
      },
      urgency: {
        type: String,
        default: 'safe',
        validator: (v) => ['critical', 'warning', 'safe'].includes(v),
      },
      alwaysClickable: {
        type: Boolean,
        default: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      // Renders the counts as plain text (no drill) — used for the Block-4 KPIs,
      // which are read-only grand totals, not navigable cells.
      isStatic: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        debouncedClick: null,
      };
    },
    computed: {
      urgencyClass() {
        return `urgency-${this.urgency}`;
      },
    },
    created() {
      this.debouncedClick = debounce(this.onClick, 300);
    },
    beforeDestroy() {
      this.debouncedClick.cancel();
    },
    methods: {
      value(card) {
        const key = card.dataKey || card.key;
        return this.data[key] || 0;
      },
      onClick(card) {
        const count = this.value(card);
        if (!count && !this.alwaysClickable) return;
        this.$emit('drill', {
          blockKey: this.definition.blockKey,
          statusKey:
            card.drillStatusKey == null ? card.key : card.drillStatusKey,
          bucketKey: card.drillBucketKey != null ? card.drillBucketKey : null,
          count,
          label: card.label || this.$t(card.labelKey),
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .monitoring-statcard-block {
    background: #2e3454;
    border-style: solid;
    border-color: rgba(#fff, 0.06);
    border-width: 1px;
    border-left-width: 4px;
    border-radius: 4px 12px 12px 4px;
    box-shadow: 0 10px 25px -5px rgba(#000, 0.3),
      0 4px 12px -2px rgba(#000, 0.2);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      transform: translateY(-3px);
      background: #333a5e;
      border-color: rgba(#fff, 0.12);
      box-shadow: 0 20px 30px -8px rgba(#000, 0.45);
    }

    ::v-deep .el-card__body {
      padding: 20px 24px;
    }

    &.urgency-critical {
      border-left-color: #ef4444;
    }

    &.urgency-warning {
      border-left-color: #f59e0b;
    }

    &.urgency-safe {
      border-left-color: #10b981;
    }
  }

  .monitoring-statcards {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  .monitoring-statcard {
    // Same self-arranging behaviour as the dashboard blocks.
    flex: 1 1 140px;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 130px;
    padding: 16px 20px;
    border: 1px solid rgba(#fff, 0.06);
    border-radius: 10px;
    background: rgba(#fff, 0.02);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      border-color: rgba(#fff, 0.12);
      background: rgba(#fff, 0.04);
    }

    &.statcard-accepted {
      background: rgba(#10b981, 0.24);
      border-color: rgba(#10b981, 0.55);

      .monitoring-statcard__icon {
        color: #34d399;
      }

      &:hover {
        background: rgba(#10b981, 0.32);
        border-color: rgba(#10b981, 0.7);
      }
    }

    &.statcard-inprogress {
      background: rgba(#f59e0b, 0.24);
      border-color: rgba(#f59e0b, 0.55);

      .monitoring-statcard__icon {
        color: #fbbf24;
      }

      &:hover {
        background: rgba(#f59e0b, 0.32);
        border-color: rgba(#f59e0b, 0.7);
      }
    }

    &.statcard-tobeverified {
      background: rgba(#6366f1, 0.24);
      border-color: rgba(#6366f1, 0.55);

      .monitoring-statcard__icon {
        color: #818cf8;
      }

      &:hover {
        background: rgba(#6366f1, 0.32);
        border-color: rgba(#6366f1, 0.7);
      }
    }

    &.statcard-tobeapproved {
      background: rgba(#64748b, 0.3);
      border-color: rgba(#64748b, 0.6);

      .monitoring-statcard__icon {
        color: #cbd5e1;
      }

      &:hover {
        background: rgba(#64748b, 0.4);
        border-color: rgba(#64748b, 0.75);
      }
    }

    &.statcard-completedunsigned {
      background: rgba(#ef4444, 0.22);
      border-color: rgba(#ef4444, 0.5);

      .monitoring-statcard__icon {
        color: #f87171;
      }

      &:hover {
        background: rgba(#ef4444, 0.3);
        border-color: rgba(#ef4444, 0.65);
      }
    }

    &.statcard-severity-info {
      background: rgba(#6366f1, 0.18);
      border-color: rgba(#6366f1, 0.45);

      .monitoring-statcard__icon {
        color: #818cf8;
      }

      &:hover {
        background: rgba(#6366f1, 0.28);
        border-color: rgba(#6366f1, 0.65);
      }
    }

    &.statcard-severity-safe {
      background: rgba(#10b981, 0.18);
      border-color: rgba(#10b981, 0.45);

      .monitoring-statcard__icon {
        color: #34d399;
      }

      &:hover {
        background: rgba(#10b981, 0.28);
        border-color: rgba(#10b981, 0.65);
      }
    }

    &.statcard-severity-warning {
      background: rgba(#f59e0b, 0.18);
      border-color: rgba(#f59e0b, 0.45);

      .monitoring-statcard__icon {
        color: #fbbf24;
      }

      &:hover {
        background: rgba(#f59e0b, 0.28);
        border-color: rgba(#f59e0b, 0.65);
      }
    }

    &.statcard-severity-critical {
      background: rgba(#ef4444, 0.18);
      border-color: rgba(#ef4444, 0.45);

      .monitoring-statcard__icon {
        color: #f87171;
      }

      &:hover {
        background: rgba(#ef4444, 0.28);
        border-color: rgba(#ef4444, 0.65);
      }
    }

    &__icon {
      font-size: 22px;
      color: #cbd5e1;
      margin-bottom: 6px;
    }

    &__label {
      font-size: 13px;
      color: #94a3b8;
      margin-bottom: 8px;
      text-align: center;
    }
  }

  // Matches the count's footprint so the card doesn't resize when data lands.
  .monitoring-count-skeleton {
    width: 56px;

    ::v-deep .el-skeleton__item {
      height: 26px;
    }
  }

  .monitoring-count {
    background: none;
    border: none;
    cursor: pointer;
    font-weight: 700;
    color: #6366f1;
    text-decoration: underline;
    padding: 0;

    &:hover {
      color: #818cf8;
    }

    &--large {
      font-size: 28px;
    }

    &--static {
      color: #f8fafc;
      cursor: default;
      text-decoration: none;

      &:hover {
        color: #f8fafc;
      }
    }

    &:disabled,
    &--zero {
      color: #64748b;
      cursor: default;
      text-decoration: none;

      &:hover {
        color: #64748b;
      }
    }
  }

  .monitoring-block__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .title {
    color: #ffffff;
    font-weight: 600;
  }
</style>
